import FindOneQuotation from "@/v2/api/quotation/FindOne";
import { useQuery } from "@tanstack/react-query";
import {
    Building2,
    Calendar,
    DollarSign,
    FileText,
    Globe,
    Hash,
    Mail,
    MapPin,
    Pencil,
    Phone,
    User,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CardContent } from "@/components/ui/card";
import ViewLayout from "@/v2/layout/View";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import { Separator } from "@/components/ui/separator";
import AccessControl from "@/v2/components/rbac/AccessControl";
import QuotationsDeleteButton from "@/v2/components/ui/button/delete/Quotations";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/v2/utils";
import PrintQuotation from "@/v2/components/pages/quotation/Print";
import { Quotation } from "@/v2/types";

const NavButtons = ({ id, data }: { id: string; data: Quotation }) => (
    <>
        <PrintQuotation data={data} isInside />
        <AccessControl module="PURCHASE_ORDER_MANAGEMENT" requestedAccess="PUT">
            <Link to={`/quotation/edit/${id}`}>
                <Button variant={"outline"} className="p-4" size={"sm"}>
                    <Pencil className="h-4 w-4" />
                    {"Edit"}
                </Button>
            </Link>
        </AccessControl>

        <AccessControl
            module="PURCHASE_ORDER_MANAGEMENT"
            requestedAccess="DELETE"
        >
            <QuotationsDeleteButton id={id} isInside />
        </AccessControl>
    </>
);

const InfoItem = ({
    icon: Icon,
    label,
    value,
}: {
    icon: any;
    label: string;
    value: string;
}) => (
    <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-gray-500" />
        <span className="font-medium text-gray-600">{label}:</span>
        <span className="font-medium">{value}</span>
    </div>
);

export default function QuotationViewOne() {
    const { id } = useParams();
    const navigate = useNavigate();

    if (!id) navigate(-1);

    const query = useQuery({
        queryKey: ["quotation", id],
        queryFn: () => FindOneQuotation(id!),
    });

    if (query.isLoading || query.isFetching) return <LoadingAnimation />;
    if (!query.data) return <p>Something went wrong</p>;

    const quotation = query.data;

    // Calculate totals

    const calculateItemTotal = (
        quantity: number,
        unitPrice: number,
        tax: number,
    ) => {
        const base = quantity * unitPrice;
        const taxAmount = base * (tax / 100);
        return base + taxAmount;
    };

    const totalAmount =
        quotation?.items?.reduce(
            (sum, item) =>
                sum +
                calculateItemTotal(item.quantity, item.unit_price, item.tax),
            0,
        ) || 0;

    return (
        <ViewLayout
            title="Quotation Details"
            description="Complete quotation information"
            icon={FileText}
            buttons={<NavButtons id={id!} data={query.data!} />}
        >
            <CardContent className="space-y-6">
                {/* Header Section */}
                <div className="grid grid-cols-2 gap-4">
                    <InfoItem
                        icon={FileText}
                        label="Quotation Number"
                        value={quotation?.quotation_number || "N/A"}
                    />
                    <InfoItem
                        icon={Calendar}
                        label="Quotation Date"
                        value={
                            quotation?.quotation_date
                                ? formatDate(quotation.quotation_date)
                                : "N/A"
                        }
                    />
                    <InfoItem
                        icon={FileText}
                        label="Reference Number"
                        value={quotation?.reference_number || "N/A"}
                    />
                    <InfoItem
                        icon={FileText}
                        label="Project"
                        value={quotation?.project_name || "N/A"}
                    />
                    <InfoItem
                        icon={FileText}
                        label="Subject"
                        value={quotation?.subject || "N/A"}
                    />
                    <InfoItem
                        icon={FileText}
                        label="Recipient"
                        value={quotation?.recipient_name || "N/A"}
                    />
                    <InfoItem
                        icon={FileText}
                        label="Recipient Designation"
                        value={quotation?.recipient_designation || "N/A"}
                    />
                </div>

                <Separator />

                {/* Client Information */}
                <div className="space-y-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold">
                        <Building2 className="h-5 w-5" />
                        Client Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 pl-4 md:grid-cols-2">
                        <InfoItem
                            icon={User}
                            label="Name"
                            value={quotation?.client?.full_name || "N/A"}
                        />
                        <InfoItem
                            icon={Mail}
                            label="Email"
                            value={quotation?.client?.email || "N/A"}
                        />
                        <InfoItem
                            icon={Phone}
                            label="Phone"
                            value={quotation?.client?.phone_number || "N/A"}
                        />
                        <InfoItem
                            icon={Hash}
                            label="CR Number"
                            value={quotation?.client?.cr_number || "N/A"}
                        />
                        <InfoItem
                            icon={Hash}
                            label="VAT Number"
                            value={quotation?.client?.vat_number || "N/A"}
                        />
                        <InfoItem
                            icon={Globe}
                            label="Website"
                            value={quotation?.client?.website || "N/A"}
                        />
                        <InfoItem
                            icon={MapPin}
                            label="Location"
                            value={quotation?.client.locations?.[0] || "N/A"}
                        />
                    </div>
                </div>

                <Separator />

                {/* Items Section */}
                <div className="space-y-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold">
                        <DollarSign className="h-5 w-5" />
                        Quotation Items
                    </h3>
                    <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="p-3 text-left font-medium text-gray-600">
                                        Sl.No
                                    </th>
                                    <th className="p-3 text-left font-medium text-gray-600">
                                        Description
                                    </th>
                                    <th className="p-3 text-left font-medium text-gray-600">
                                        Unit
                                    </th>
                                    <th className="p-3 text-right font-medium text-gray-600">
                                        Quantity
                                    </th>
                                    <th className="p-3 text-right font-medium text-gray-600">
                                        Unit Price
                                    </th>
                                    <th className="p-3 text-right font-medium text-gray-600">
                                        Tax
                                    </th>
                                    <th className="p-3 text-right font-medium text-gray-600">
                                        Net Amount
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {query?.data?.items?.map((item) => (
                                    <tr
                                        key={item.description}
                                        className="bg-white"
                                    >
                                        <td className="p-3">{item.sno}</td>

                                        <td className="p-3">
                                            {item.description}
                                        </td>
                                        <td className="p-3">{item.uom}</td>
                                        <td className="p-3 text-right">
                                            {item.quantity}
                                        </td>
                                        <td className="p-3 text-right">
                                            {item.unit_price?.toFixed(2)} SR
                                        </td>
                                        <td className="p-3 text-right">
                                            {item.tax?.toFixed(2)} %
                                        </td>
                                        <td className="p-3 text-right">
                                            {(
                                                item.unit_price *
                                                item.quantity *
                                                (1 + item.tax / 100)
                                            ).toFixed(2)}{" "}
                                            SR
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="p-3 text-right font-semibold"
                                    >
                                        Total Amount:
                                    </td>
                                    <td className="p-3 text-right font-semibold">
                                        {totalAmount.toFixed(2)} SR
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Terms and Conditions */}
                {quotation?.terms_and_conditions &&
                    quotation.terms_and_conditions.length > 0 && (
                        <div className="mt-6 rounded-lg bg-gray-50 p-4">
                            <h4 className="mb-2 font-medium">
                                Terms and Conditions
                            </h4>
                            {quotation.terms_and_conditions.map(
                                (term, index) => (
                                    <span
                                        key={index}
                                        className="flex items-start justify-start gap-1"
                                    >
                                        <span>{index + 1}. </span>
                                        <p className="text-gray-700">{term}</p>
                                    </span>
                                ),
                            )}
                        </div>
                    )}
            </CardContent>
        </ViewLayout>
    );
}
